import { Loader2 } from "lucide-react";

/** Inline spinner row — use inside fixed-height containers, never as a full-page swap. */
export function LoadingIndicator({ label = "Loading…", testId }) {
    return (
        <div className="flex items-center justify-center gap-2 text-sm text-white/40" data-testid={testId}>
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            <span>{label}</span>
        </div>
    );
}

/**
 * Fixed-height content region with absolute layers (content + loading overlay).
 * `layout="stack"` — bounded frame with internal scroll (empty/loading states).
 * `layout="flow"` — natural document height; page scroll lives in AppLayout main.
 */
export function ShellContentFrame({
    loading = false,
    loadingLabel = "Loading…",
    children,
    className = "",
    minHeight = "min-h-[28rem]",
    layout = "stack",
    testId,
    loadingTestId,
}) {
    const isFlow = layout === "flow";

    return (
        <div
            className={`shell-content-frame ${isFlow ? "shell-content-frame--flow" : ""} ${loading && isFlow ? "shell-content-frame--loading" : ""} relative w-full ${isFlow ? "" : "min-h-0 flex-1"} ${minHeight} ${className}`}
            data-testid={testId}
        >
            <div
                className={`shell-content-frame__body ${isFlow ? "shell-content-frame__body--flow" : "absolute inset-0 overflow-auto"} ${loading ? "pointer-events-none invisible" : ""}`}
                aria-hidden={loading}
            >
                {children}
            </div>
            <div
                className={`shell-content-frame__overlay absolute inset-0 grid place-items-center ${loading ? "" : "pointer-events-none invisible"}`}
                aria-hidden={!loading}
                data-testid={loadingTestId}
            >
                <LoadingIndicator label={loadingLabel} />
            </div>
        </div>
    );
}

/**
 * Mutually exclusive view inside ShellContentFrame — all panes stay mounted;
 * only visibility toggles so frame height never thrashes.
 */
export function ShellStatePane({ active, children, testId, layout = "stack" }) {
    const isFlow = layout === "flow";

    return (
        <div
            className={`shell-state-pane ${isFlow ? "shell-state-pane--flow" : ""}${active ? " is-active" : ""}`}
            aria-hidden={!active}
            data-testid={testId}
        >
            {children}
        </div>
    );
}
